import React, { useState, useEffect } from 'react';
import { Card, Avatar, Tag, Rate, message, Empty, Spin } from 'antd';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { UserOutlined, DollarOutlined } from '@ant-design/icons';
import apiService from 'services/api/apiService';

interface Application {
  id: number;
  applicant_name: string;
  applicant_email: string;
  expected_salary: number;
  experience_years: number;
  status: string;
  job_id: number;
  job_title?: string;
}

const STAGES = [
  { key: 'submitted',  label: 'New Applications', color: '#3b82f6' },
  { key: 'screening',  label: 'Screening',         color: '#f59e0b' },
  { key: 'interview',  label: 'Interview',          color: '#10b981' },
  { key: 'assessment', label: 'Assessment',         color: '#722ed1' },
  { key: 'offer',      label: 'Offer',              color: '#8b5cf6' },
  { key: 'hired',      label: 'Hired',              color: '#13c2c2' },
];

const fmt = (v: number) => v ? `₦${Number(v).toLocaleString()}` : '—';

const ApplicationsKanban: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchApplications(); }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await apiService.getJobApplications();
      let data = res.data || res.applications || [];
      if (!Array.isArray(data)) data = [];
      setApplications(data);
    } catch (e) {
      console.error('Kanban fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const appId = parseInt(draggableId, 10);
    const newStatus = destination.droppableId;

    // Optimistic update
    setApplications(prev =>
      prev.map(a => a.id === appId ? { ...a, status: newStatus } : a)
    );

    const res = await apiService.updateApplicationStatus(appId, newStatus);
    if (!res.success) {
      message.error('Failed to update status — reverting');
      fetchApplications();
    } else {
      message.success(`Moved to ${STAGES.find(s => s.key === newStatus)?.label}`);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12 }}>
        {STAGES.map(stage => {
          const stageApps = applications.filter(a => a.status === stage.key);
          return (
            <div key={stage.key} style={{ minWidth: 260, flex: '0 0 260px' }}>
              <div style={{
                background: stage.color, color: 'white', padding: '10px 14px',
                borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', fontWeight: 600,
              }}>
                <span>{stage.label}</span>
                <Tag style={{ background: 'rgba(255,255,255,0.25)', color: 'white', border: 'none' }}>
                  {stageApps.length}
                </Tag>
              </div>
              <Droppable droppableId={stage.key}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      background: snapshot.isDraggingOver ? '#f0f5ff' : '#fafafa',
                      minHeight: 400, padding: 8, borderRadius: '0 0 8px 8px',
                      border: '1px solid #f0f0f0', borderTop: 'none',
                      transition: 'background 0.2s',
                    }}
                  >
                    {stageApps.length === 0 && (
                      <Empty
                        description="No applicants"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ marginTop: 40 }}
                      />
                    )}
                    {stageApps.map((app, index) => (
                      <Draggable key={app.id} draggableId={String(app.id)} index={index}>
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            style={{
                              marginBottom: 8,
                              ...dragProvided.draggableProps.style,
                            }}
                          >
                            <Card
                              size="small"
                              hoverable
                              style={{
                                boxShadow: dragSnapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <Avatar size="small" icon={<UserOutlined />} style={{ background: stage.color }} />
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: 13 }}>{app.applicant_name}</div>
                                  <div style={{ fontSize: 11, color: '#888' }}>{app.job_title || 'Position'}</div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666' }}>
                                <span><DollarOutlined /> {fmt(app.expected_salary)}</span>
                                <span>{app.experience_years || 0}y exp</span>
                              </div>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default ApplicationsKanban;
